import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useGetBillingCustomerQuery, useGetPlansQuery, useStartDeltaTrialMutation } from '../../../store/plan/plan'

import { useNavigate, useParams } from 'react-router-dom'
import { DELTA_TRIAL_FEATURES } from './DeltaTrialFeatures'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { Button } from '../../../components/Button/Button'
import { Divider } from '../../../components/Divider/Divider'
import DeltaTrialLogo1 from '../../../assets/images/delta-trial-logo1.png'
import DeltaTrialLogo2 from '../../../assets/images/delta-trial-logo2.png'
import DeltaTrialLogo3 from '../../../assets/images/delta-trial-logo3.png'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { createPortal } from 'react-dom'
import { DeltaTrialHeader } from './DeltaTrialHeader'
import { DeltaTrialLoader } from './Loader/DeltaTrialLoader'
import { Plan } from '../../../models/Plan'
import { mapEnterpriseBillingOptions } from '../utils'
import { formatPrice, getProratedPrice } from './utils'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { addDaysToDate, addMonthsToDate, addYearsToDate, formatDate } from '../../../common/dateUtils'
import './DeltaTrial.scss'

export const DeltaTrial: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)

  const navigate = useNavigate()

  const { portalRef } = useContext(HeaderContext)

  const { feature } = useParams()

  const { tracked } = useTracking()

  const [pollingInterval, setPollingInterval] = useState<number>(0)

  const { data: globalPlans } = useGetPlansQuery()
  const { data: customer } = useGetBillingCustomerQuery({ accountId }, { refetchOnMountOrArgChange: true, pollingInterval })

  const [triggerStartDeltaTrial] = useStartDeltaTrialMutation()

  const plans = useMemo((): Plan[] => {
    if (!globalPlans || !customer) {
      return []
    }
    if (!customer.data.enterpriseSubscription) {
      return globalPlans.data
    }
    const { package: enterpriseBillingPackage } = customer.data.enterpriseSubscription.enterpriseBillingConfig
    return mapEnterpriseBillingOptions(enterpriseBillingPackage, enterpriseBillingPackage.billingOptions, globalPlans.data)
  }, [globalPlans, customer])

  useEffect(() => {
    if (!customer?.data) {
      return
    }
    if (customer.data.deltaTrial) {
      navigate(`/account/plan/free-trial/${feature}/success`)
    }
  }, [customer, feature, navigate])

  if (!feature || !DELTA_TRIAL_FEATURES[feature]) {
    return <div>Delta Trial feature not found</div>
  }

  const { name, planId, DeltaTrialTimeline } = DELTA_TRIAL_FEATURES[feature]

  if (!plans.length || !customer) {
    return (
      <div className='d-flex align-items-center justify-content-center truly-full-size-content delta-trial-body'>
        <DeltaTrialLoader message='Preparing your free trial details…' />
      </div>
    )
  }

  const { subscription, deltaTrial } = customer.data

  if (!subscription) {
    return <div>No subscription found</div>
  }

  if (deltaTrial && !pollingInterval) {
    return <div>Free trial already started</div>
  }

  const deltaTrialPlan = plans.find((plan) => plan.id === planId)
  const currentPlan = plans.find((plan) => plan.id === subscription.planId)

  if (!deltaTrialPlan) {
    return <div>Plan not found for delta trial</div>
  }

  if (!deltaTrialPlan.deltaTrialDays) {
    return <div>Plan does not have delta trial</div>
  }

  if (!currentPlan) {
    return <div>Current plan not found</div>
  }

  const {
    numberOfSeats: seats,
    additionalProducts: { assistant: { quantity: assistantSeats = 0 } = {} } = {},
    period,
    currentPeriodStart,
    currentPeriodEnd,
  } = subscription

  const currentSeatPrice = currentPlan.prices.find((price) => price.period === period)
  const currentAssistantProduct = currentPlan.additionalProducts.find((product) => product.type === 'assistant')
  const currentAssistantSeatPrice = currentAssistantProduct?.prices.find((price) => price.period === period)
  const deltaSeatPrice = deltaTrialPlan.prices.find((price) => price.period === period)
  const deltaAssistantProduct = deltaTrialPlan.additionalProducts.find((product) => product.type === 'assistant')
  const deltaAssistantSeatPrice = deltaAssistantProduct?.prices.find((price) => price.period === period)

  const currentSeatFullAmount = currentSeatPrice?.billingScheme === 'perUnit' ? (seats * currentSeatPrice.amount) / 100 : 0
  const currentAssistantSeatFullAmount = currentAssistantSeatPrice?.billingScheme === 'perUnit' ? (assistantSeats * currentAssistantSeatPrice.amount) / 100 : 0
  const [deltaSeatUnitAmount, deltaSeatFullAmount] =
    deltaSeatPrice?.billingScheme === 'perUnit' ? [deltaSeatPrice.amount / 100, (seats * deltaSeatPrice.amount) / 100] : [0, 0]
  const [deltaAssistantSeatUnitAmount, deltaAssistantSeatFullAmount] =
    deltaAssistantSeatPrice?.billingScheme === 'perUnit' && assistantSeats
      ? [deltaAssistantSeatPrice.amount / 100, (assistantSeats * deltaAssistantSeatPrice.amount) / 100]
      : [0, 0]

  const proratedPrice = deltaSeatFullAmount
    ? getProratedPrice(
        new Date(currentPeriodStart),
        new Date(currentPeriodEnd),
        addDaysToDate(new Date(), deltaTrialPlan.deltaTrialDays),
        deltaSeatFullAmount + (deltaAssistantSeatFullAmount ?? 0) - currentSeatFullAmount - (currentAssistantSeatFullAmount ?? 0),
      )
    : null

  const getNextPeriodEndAfterDeltaTrial = () => {
    const currentPeriodEndsAt = new Date(currentPeriodEnd)
    if (!deltaTrialPlan.deltaTrialDays) {
      return currentPeriodEndsAt
    }
    const deltaTrialEndsAt = addDaysToDate(new Date(), deltaTrialPlan.deltaTrialDays)
    if (deltaTrialEndsAt < currentPeriodEndsAt) {
      return currentPeriodEndsAt
    }
    return period === 'month' ? addMonthsToDate(currentPeriodEndsAt, 1) : addYearsToDate(currentPeriodEndsAt, 1)
  }

  const _handleStartDeltaTrial = async () => {
    try {
      setPollingInterval(5000)
      await triggerStartDeltaTrial({ accountId, planId: deltaTrialPlan.id }).unwrap()
    } catch (e) {
      console.error(e)
      setPollingInterval(0)
    }
  }

  const handleStartDeltaTrial = tracked(_handleStartDeltaTrial, {
    eventName: TrackEvent.PLAN_DELTA_TRAL_STARTED,
    eventDetails: { planId: deltaTrialPlan.id },
  })

  return (
    <>
      {portalRef && portalRef.current ? createPortal(<DeltaTrialHeader />, portalRef.current) : null}
      <div className='truly-full-size-content delta-trial-body zocks-bg-color-gray-100'>
        <div className='d-flex align-items-center justify-content-center h-100'>
          <div className='d-flex flex-column flex-lg-row align-items-center zocks-gap-4 gap-lg-4'>
            <div className='d-flex flex-column py-4 gap-4 zocks-border-0.75 border border-1 zocks-border-color-gray-100 zocks-bg-color-white delta-trial-left-container'>
              <div className='d-flex flex-column px-4 gap-2'>
                <h5>
                  Start your {deltaTrialPlan.deltaTrialDays}-day free <span className='zocks-text-primary'>{deltaTrialPlan.name}</span> trial
                </h5>
                <div className='paragraph-medium'>You'll get full access to {name} immediately. Here's your estimated pricing based on your current team:</div>
              </div>
              <div className='d-flex flex-column px-4'>
                <div className='d-flex align-items-center justify-content-between py-3 border-bottom border-1 zocks-border-color-gray-300'>
                  <div className='d-flex flex-column gap-1'>
                    <div className='paragraph-medium'>Advisor seats</div>
                    <div className='paragraph-small zocks-text-gray-600'>
                      {deltaSeatUnitAmount ? `${seats} x ` + formatPrice(deltaSeatUnitAmount) + `/seat/${period === 'month' ? 'mo' : 'yr'}` : null}
                    </div>
                  </div>
                  <div className='paragraph-medium'>{deltaSeatFullAmount ? formatPrice(deltaSeatFullAmount) : 'Unknown'}</div>
                </div>
                {deltaAssistantSeatFullAmount ? (
                  <div className='d-flex align-items-center justify-content-between py-3 border-bottom border-1 zocks-border-color-gray-300'>
                    <div className='d-flex flex-column gap-1'>
                      <div className='paragraph-medium'>Assistant seats</div>
                      <div className='paragraph-small zocks-text-gray-600'>
                        {deltaAssistantSeatUnitAmount
                          ? `${assistantSeats} x ` + formatPrice(deltaAssistantSeatUnitAmount) + `/seat/${period === 'month' ? 'mo' : 'yr'}`
                          : null}
                      </div>
                    </div>
                    <div className='paragraph-medium'>
                      {currentAssistantSeatPrice?.billingScheme === 'perUnit' ? formatPrice(deltaAssistantSeatFullAmount) : 'Unknown'}
                    </div>
                  </div>
                ) : null}
                <div className='d-flex align-items-center justify-content-between py-3 border-bottom border-1 zocks-border-color-gray-300'>
                  <div className='d-flex flex-column gap-1'>
                    <div className='paragraph-medium bold'>Estimated {period === 'month' ? 'monthly' : 'yearly'} fee</div>
                    <div className='paragraph-small zocks-text-gray-600'>from {formatDate(getNextPeriodEndAfterDeltaTrial())}</div>
                  </div>
                  <div className='paragraph-medium'>
                    {deltaSeatFullAmount ? formatPrice(deltaSeatFullAmount + (deltaAssistantSeatFullAmount ?? 0)) : 'Unknown'}
                  </div>
                </div>
              </div>
              <div className='d-flex zocks-px-0.75 flex-grow-1'>
                <div className='d-flex zocks-px-0.75 py-4 zocks-bg-color-gray-100 border border-1 zocks-border-0.5 zocks-border-color-gray-300 flex-grow-1 justify-content-between'>
                  <div className='d-flex gap-2 align-items-center'>
                    <h6>Due at end of trial</h6>
                    <Tooltip
                      className='paragraph-small position-fixed'
                      title='Prorated upgrade fee for remaining days in your current billing cycle '
                      placement='top'
                    >
                      <i className='bi bi-info-square fs-6' />
                    </Tooltip>
                  </div>
                  <h6>{proratedPrice ? formatPrice(proratedPrice) : 'Unknown'}</h6>
                </div>
              </div>
              <div className='d-flex flex-column zocks-gap-0.75 align-items-center'>
                <Button variant='primary' loading={!!pollingInterval} onClick={handleStartDeltaTrial}>
                  Start free trial now
                </Button>
                <div className='paragraph-small'>No strings attached, cancel anytime.</div>
              </div>
            </div>
            <div className='d-flex flex-column delta-trial-right-container'>
              <DeltaTrialTimeline proratedPrice={proratedPrice} deltaTrialDays={deltaTrialPlan.deltaTrialDays} />
              <Divider className='my-4' />
              <div className='d-flex flex-column gap-2 align-items-center mt-2'>
                <div className='paragraph-small'>
                  Trusted by <strong>thousands of advisors & firms</strong> of every size
                </div>
                <div className='d-flex gap-3 justify-content-center delta-trial-logo-container'>
                  <img src={DeltaTrialLogo1} alt='Carson' />
                  <img src={DeltaTrialLogo2} alt='Commonwealth' />
                  <img src={DeltaTrialLogo3} alt='Legacy Financial' />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
